import { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import AutocompleteDropdown from '../AutocompleteDropdown/AutocompleteDropdown';
import {
    getSupplementNames, getSupplementData, catalogToEntry,
    getAllCategories, getSupplementsByCategories,
} from '../../util/ProtocolUtil';
import { formatCategoryName } from '../../util/RecUtil';

/** Shared keyboard handler for arrow-key / Enter / Escape dropdown navigation. */
function makeDropdownKeyHandler({ items, selectedIndex, setSelectedIndex, onSelect, onClose }) {
    return (e) => {
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setSelectedIndex(prev => prev < items.length - 1 ? prev + 1 : prev);
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
                break;
            case 'Enter':
                e.preventDefault();
                if (selectedIndex >= 0 && items[selectedIndex]) onSelect(items[selectedIndex]);
                break;
            case 'Escape':
                onClose();
                break;
        }
    };
}

const DROPDOWN_POSITION = { top: '100%', left: 0, right: 0 };

function AddSupplementRow({ onAdd, existingNames = [] }) {
    // Expand / collapse
    const [expanded, setExpanded] = useState(false);

    // Custom entry creation
    const [showCustomForm, setShowCustomForm] = useState(false);
    const [customFields, setCustomFields] = useState({
        name: '', dose: '', unit: 'capsule', frequency: '1', freqUnit: 'day', category: '',
    });
    const [showCustomCatDropdown, setShowCustomCatDropdown] = useState(false);
    const [customCatSelectedIndex, setCustomCatSelectedIndex] = useState(-1);

    // Name autocomplete state
    const [inputValue, setInputValue] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [showDropdown, setShowDropdown] = useState(false);
    const [warning, setWarning] = useState(null);
    const nameInputRef = useRef(null);

    // Category multi-select state
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [categoryInput, setCategoryInput] = useState('');
    const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
    const [categorySelectedIndex, setCategorySelectedIndex] = useState(-1);
    const categoryInputRef = useRef(null);

    // All categories (stable — getAllCategories is module-level cached)
    const allCategories = useMemo(() => getAllCategories(), []);

    // Filtered category dropdown options
    const filteredCategories = useMemo(() => {
        const lower = categoryInput.toLowerCase();
        return allCategories
            .filter(c => !selectedCategories.includes(c))
            .filter(c => !lower || formatCategoryName(c).toLowerCase().includes(lower));
    }, [allCategories, selectedCategories, categoryInput]);

    // Display name → raw slug lookup map (avoids O(n) reverse search on select)
    const categoryDisplayToSlug = useMemo(() => {
        const map = new Map();
        filteredCategories.forEach(c => map.set(formatCategoryName(c), c));
        return map;
    }, [filteredCategories]);

    // Supplements matching all selected categories
    const categoryMatchedSupplements = useMemo(() => {
        return getSupplementsByCategories(selectedCategories, existingNames);
    }, [selectedCategories, existingNames]);

    // Auto-focus name input on expand
    useEffect(() => {
        if (expanded) {
            requestAnimationFrame(() => nameInputRef.current?.focus());
        }
    }, [expanded]);

    // --- Name autocomplete ---
    const filterSuggestions = useCallback((value) => {
        if (!value.trim()) {
            setSuggestions([]);
            setShowDropdown(false);
            return;
        }

        const lower = value.toLowerCase();
        const existing = new Set(existingNames.map(n => n.toLowerCase()));

        // When categories are selected, constrain to matching supplements
        const pool = selectedCategories.length > 0
            ? categoryMatchedSupplements
            : getSupplementNames();

        const filtered = pool
            .filter(name => !existing.has(name.toLowerCase()))
            .filter(name => name.toLowerCase().includes(lower));

        filtered.sort((a, b) => {
            const aLower = a.toLowerCase();
            const bLower = b.toLowerCase();
            const aExact = aLower === lower;
            const bExact = bLower === lower;
            if (aExact !== bExact) return aExact ? -1 : 1;
            const aStarts = aLower.startsWith(lower);
            const bStarts = bLower.startsWith(lower);
            if (aStarts !== bStarts) return aStarts ? -1 : 1;
            return a.localeCompare(b);
        });

        setSuggestions(filtered);
        setSelectedIndex(-1);
        setShowDropdown(filtered.length > 0);
    }, [existingNames, selectedCategories, categoryMatchedSupplements]);

    const handleSelect = useCallback((name) => {
        const data = getSupplementData(name);
        if (!data) return;

        if (data.contraindications?.length > 0) {
            setWarning(`Contraindicated: ${data.contraindications.join(', ')}`);
        } else {
            setWarning(null);
        }

        const entry = catalogToEntry(name, data);
        onAdd(entry);
        setInputValue('');
        setSuggestions([]);
        setShowDropdown(false);
        setSelectedIndex(-1);

        if (data.contraindications?.length > 0) {
            setTimeout(() => setWarning(null), 5000);
        }
    }, [onAdd]);

    const handleInputChange = (e) => {
        const value = e.target.value;
        setInputValue(value);
        setWarning(null);
        filterSuggestions(value);
    };

    const handleKeyDown = (e) => {
        if (!showDropdown) return;
        makeDropdownKeyHandler({
            items: suggestions,
            selectedIndex,
            setSelectedIndex,
            onSelect: handleSelect,
            onClose: () => { setShowDropdown(false); setSelectedIndex(-1); },
        })(e);
    };

    // --- Category multi-select ---
    const handleAddCategory = useCallback((cat) => {
        setSelectedCategories(prev => [...prev, cat]);
        setCategoryInput('');
        setCategorySelectedIndex(-1);
        setShowCategoryDropdown(true);
    }, []);

    const handleRemoveCategory = useCallback((cat) => {
        setSelectedCategories(prev => prev.filter(c => c !== cat));
    }, []);

    const handleCategoryInputChange = (e) => {
        setCategoryInput(e.target.value);
        setCategorySelectedIndex(-1);
        setShowCategoryDropdown(true);
    };

    const handleCategoryKeyDown = (e) => {
        if (e.key === 'Backspace' && !categoryInput && selectedCategories.length > 0) {
            setSelectedCategories(prev => prev.slice(0, -1));
            return;
        }
        if (!showCategoryDropdown || filteredCategories.length === 0) return;
        makeDropdownKeyHandler({
            items: filteredCategories,
            selectedIndex: categorySelectedIndex,
            setSelectedIndex: setCategorySelectedIndex,
            onSelect: handleAddCategory,
            onClose: () => { setShowCategoryDropdown(false); setCategorySelectedIndex(-1); },
        })(e);
    };

    // --- Custom entry ---
    const filteredCustomCategories = useMemo(() => {
        const lower = customFields.category.toLowerCase();
        if (!lower) return allCategories;
        return allCategories.filter(c => formatCategoryName(c).toLowerCase().includes(lower));
    }, [allCategories, customFields.category]);

    const customCatDisplayToSlug = useMemo(() => {
        const map = new Map();
        filteredCustomCategories.forEach(c => map.set(formatCategoryName(c), c));
        return map;
    }, [filteredCustomCategories]);

    const handleCustomFieldChange = (field, value) => {
        setCustomFields(prev => ({ ...prev, [field]: value }));
        if (field === 'category') {
            setCustomCatSelectedIndex(-1);
            setShowCustomCatDropdown(true);
        }
    };

    const handleCustomAdd = () => {
        if (!customFields.name.trim()) return;
        const entry = {
            name: customFields.name.trim(),
            category: customFields.category.trim() || 'uncategorized',
            dose: parseInt(customFields.dose) || 1,
            frequency: parseInt(customFields.frequency) || 1,
            dailyTotal: (parseInt(customFields.dose) || 1) * (parseInt(customFields.frequency) || 1),
            unit: customFields.unit.trim() || 'capsule',
            freqUnit: customFields.freqUnit.trim() || 'day',
            emptyStomach: false,
            instructions: null,
            notes: null,
            // Provenance is manual; author is stamped to the acting role by the
            // editor's handleAddSupplement.
            source: 'manual',
        };
        onAdd(entry);
        setCustomFields({ name: '', dose: '', unit: 'capsule', frequency: '1', freqUnit: 'day', category: '' });
        setShowCustomForm(false);
        setShowCustomCatDropdown(false);
        setCustomCatSelectedIndex(-1);
    };

    // --- Expand / Collapse ---
    const handleToggleExpand = () => {
        if (expanded) {
            setExpanded(false);
            setInputValue('');
            setSuggestions([]);
            setShowDropdown(false);
            setSelectedCategories([]);
            setCategoryInput('');
            setShowCategoryDropdown(false);
            setWarning(null);
            setShowCustomForm(false);
            setCustomFields({ name: '', dose: '', unit: 'capsule', frequency: '1', freqUnit: 'day', category: '' });
            setShowCustomCatDropdown(false);
            setCustomCatSelectedIndex(-1);
        } else {
            setExpanded(true);
        }
    };

    return (
        <div className={`protocol-add-row ${expanded ? 'expanded' : ''}`}>
            <div className="protocol-add-header" onClick={handleToggleExpand}>
                <span className="protocol-add-icon">{expanded ? '\u2212' : '+'}</span>
                <span>Add supplement</span>
                {expanded && (
                    <button
                        className="protocol-add-create-new"
                        onClick={(e) => {
                            e.stopPropagation();
                            setShowCustomForm(!showCustomForm);
                        }}
                    >
                        Create New
                    </button>
                )}
                {expanded && (
                    <svg
                        className="protocol-add-collapse"
                        width="16" height="16" viewBox="0 0 16 16"
                        fill="none" stroke="currentColor" strokeWidth="1.5"
                    >
                        <path d="M4 10 L8 6 L12 10" />
                    </svg>
                )}
            </div>

            {expanded && (
                <div className="protocol-add-expanded">
                    {/* Name input */}
                    <div className="protocol-add-name-wrapper">
                        <input
                            ref={nameInputRef}
                            type="text"
                            placeholder="Search by name..."
                            value={inputValue}
                            onChange={handleInputChange}
                            onKeyDown={handleKeyDown}
                            onFocus={() => inputValue && filterSuggestions(inputValue)}
                            onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                        />
                        {showDropdown && (
                            <AutocompleteDropdown
                                suggestions={suggestions}
                                onSelect={handleSelect}
                                selectedIndex={selectedIndex}
                                position={DROPDOWN_POSITION}
                                onClose={() => setShowDropdown(false)}
                            />
                        )}
                    </div>

                    {/* Category multi-select */}
                    <div className="protocol-add-categories">
                        <div
                            className="protocol-add-category-input-wrapper"
                            onClick={() => categoryInputRef.current?.focus()}
                        >
                            {selectedCategories.map(cat => (
                                <span key={cat} className="protocol-add-category-tag">
                                    {formatCategoryName(cat)}
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleRemoveCategory(cat);
                                        }}
                                    >
                                        &times;
                                    </button>
                                </span>
                            ))}
                            <input
                                ref={categoryInputRef}
                                type="text"
                                placeholder={selectedCategories.length ? '' : 'Filter by category...'}
                                value={categoryInput}
                                onChange={handleCategoryInputChange}
                                onKeyDown={handleCategoryKeyDown}
                                onFocus={() => setShowCategoryDropdown(true)}
                                onBlur={() => setTimeout(() => setShowCategoryDropdown(false), 200)}
                            />
                        </div>
                        {showCategoryDropdown && filteredCategories.length > 0 && (
                            <AutocompleteDropdown
                                suggestions={filteredCategories.map(formatCategoryName)}
                                onSelect={(displayName) => {
                                    const raw = categoryDisplayToSlug.get(displayName);
                                    if (raw) handleAddCategory(raw);
                                }}
                                selectedIndex={categorySelectedIndex}
                                position={DROPDOWN_POSITION}
                                onClose={() => setShowCategoryDropdown(false)}
                            />
                        )}
                    </div>

                    {/* Supplement tiles (when categories selected) */}
                    {selectedCategories.length > 0 && categoryMatchedSupplements.length > 0 && (
                        <div className="protocol-row-tiles">
                            {categoryMatchedSupplements.map(name => {
                                const data = getSupplementData(name);
                                return (
                                    <div
                                        key={name}
                                        className="protocol-row-tile"
                                        onClick={() => handleSelect(name)}
                                    >
                                        <span className="tile-name">{name}</span>
                                        <span className="tile-dose">
                                            {data?.min_daily_dose || 1} {data?.unit || 'capsule'}/day
                                        </span>
                                        <div className="tile-cats">
                                            {(data?.category?.primary || []).map(c => (
                                                <span key={c} className="tile-cat-tag">
                                                    {formatCategoryName(c)}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {selectedCategories.length > 0 && categoryMatchedSupplements.length === 0 && (
                        <div className="protocol-add-no-matches">
                            No supplements match all selected categories.
                        </div>
                    )}

                    {warning && (
                        <div className="protocol-add-warning">{warning}</div>
                    )}

                    {showCustomForm && (
                        <div className="protocol-add-custom-form">
                            <div className="protocol-add-custom-row">
                                <div className="protocol-add-custom-field">
                                    <label>Name</label>
                                    <input
                                        type="text"
                                        placeholder="Supplement name"
                                        value={customFields.name}
                                        onChange={(e) => handleCustomFieldChange('name', e.target.value)}
                                    />
                                </div>
                                <div className="protocol-add-custom-field" style={{ position: 'relative' }}>
                                    <label>Category</label>
                                    <input
                                        type="text"
                                        placeholder="e.g. digestive support"
                                        value={customFields.category}
                                        onChange={(e) => handleCustomFieldChange('category', e.target.value)}
                                        onFocus={() => setShowCustomCatDropdown(true)}
                                        onBlur={() => setTimeout(() => setShowCustomCatDropdown(false), 200)}
                                        onKeyDown={(e) => {
                                            if (!showCustomCatDropdown || filteredCustomCategories.length === 0) return;
                                            makeDropdownKeyHandler({
                                                items: filteredCustomCategories,
                                                selectedIndex: customCatSelectedIndex,
                                                setSelectedIndex: setCustomCatSelectedIndex,
                                                onSelect: (slug) => {
                                                    setCustomFields(prev => ({ ...prev, category: formatCategoryName(slug) }));
                                                    setShowCustomCatDropdown(false);
                                                    setCustomCatSelectedIndex(-1);
                                                },
                                                onClose: () => { setShowCustomCatDropdown(false); setCustomCatSelectedIndex(-1); },
                                            })(e);
                                        }}
                                    />
                                    {showCustomCatDropdown && filteredCustomCategories.length > 0 && (
                                        <AutocompleteDropdown
                                            suggestions={filteredCustomCategories.map(formatCategoryName)}
                                            onSelect={(displayName) => {
                                                const raw = customCatDisplayToSlug.get(displayName);
                                                if (raw) {
                                                    setCustomFields(prev => ({ ...prev, category: formatCategoryName(raw) }));
                                                    setShowCustomCatDropdown(false);
                                                    setCustomCatSelectedIndex(-1);
                                                }
                                            }}
                                            selectedIndex={customCatSelectedIndex}
                                            position={DROPDOWN_POSITION}
                                            onClose={() => setShowCustomCatDropdown(false)}
                                        />
                                    )}
                                </div>
                            </div>
                            <div className="protocol-add-custom-row">
                                <div className="protocol-add-custom-field">
                                    <label>Dose</label>
                                    <input
                                        type="number"
                                        min="1"
                                        placeholder="1"
                                        value={customFields.dose}
                                        onChange={(e) => handleCustomFieldChange('dose', e.target.value)}
                                    />
                                </div>
                                <div className="protocol-add-custom-field">
                                    <label>Unit</label>
                                    <input
                                        type="text"
                                        placeholder="capsule"
                                        value={customFields.unit}
                                        onChange={(e) => handleCustomFieldChange('unit', e.target.value)}
                                    />
                                </div>
                                <div className="protocol-add-custom-field">
                                    <label>Frequency</label>
                                    <input
                                        type="number"
                                        min="1"
                                        placeholder="1"
                                        value={customFields.frequency}
                                        onChange={(e) => handleCustomFieldChange('frequency', e.target.value)}
                                    />
                                </div>
                                <div className="protocol-add-custom-field">
                                    <label>Per</label>
                                    <select
                                        value={customFields.freqUnit}
                                        onChange={(e) => handleCustomFieldChange('freqUnit', e.target.value)}
                                    >
                                        <option value="day">day</option>
                                        <option value="meal">meal</option>
                                        <option value="week">week</option>
                                    </select>
                                </div>
                            </div>
                            <button
                                className="protocol-add-custom-submit"
                                disabled={!customFields.name.trim()}
                                onClick={handleCustomAdd}
                            >
                                Add to Protocol
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default AddSupplementRow;
